import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { fmtFechaHora } from '../utils/fecha'
import { marketingApi } from '../api/marketing'
import { useAutoRefresh } from '../hooks/useAutoRefresh'
import Boton from '../components/Boton'

export default function MktCampanaDetallePage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [c, setC] = useState(null)
  const [err, setErr] = useState('')
  const [accionando, setAccionando] = useState(false)

  const cargar = useCallback(async () => {
    const r = await marketingApi.campana(id)
    setC(r.data)
  }, [id])

  useEffect(() => { cargar() }, [cargar])
  useAutoRefresh(cargar, 15000)

  const accion = async (fn, confirmMsg = null) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return
    setAccionando(true); setErr('')
    try { await fn(); await cargar() }
    catch (e) { setErr(e.response?.data?.message || 'Error') }
    finally { setAccionando(false) }
  }

  if (!c) return <p style={{ color: '#94a3b8' }}>Cargando…</p>

  const m = c.metricas || {}
  const editable = ['borrador', 'programada'].includes(c.estado)
  const kpis = [
    { label: 'Destinatarios', valor: m.destinatarios ?? 0 },
    { label: 'Enviados', valor: m.enviados ?? 0 },
    { label: 'Aperturas', valor: `${m.abiertos ?? 0} (${m.tasa_apertura ?? 0}%)` },
    { label: 'Clicks', valor: `${m.con_click ?? 0} (${m.tasa_click ?? 0}%)` },
    { label: 'Bounces', valor: m.bounces ?? 0, alerta: (m.bounces ?? 0) > 0 },
    { label: 'Bajas', valor: m.bajas ?? 0 },
  ]

  return (
    <div style={{ maxWidth: 980 }}>
      <Boton variante="ghost" sm style={{ marginBottom: 8 }} onClick={() => navigate(-1)}>← Volver</Boton>
      <div style={st.head}>
        <div>
          <h2 style={st.title}>{c.nombre}</h2>
          <p style={st.sub}>
            <strong>{c.asunto}</strong> · Lista: {c.lista?.nombre || '—'} · Estado: <strong>{c.estado}</strong>
            {c.programada_at && c.estado === 'programada' && <> · sale {fmtFechaHora(c.programada_at)}</>}
          </p>
        </div>
        <div style={st.botones}>
          {editable && <Boton variante="secondary" onClick={() => navigate(`/marketing/campanas/${id}/editar`)}>✏️ Editar</Boton>}
          {editable && (
            <Boton variante="primary" disabled={accionando}
              onClick={() => accion(() => marketingApi.enviarCampana(id, true),
                `¿Enviar "${c.nombre}" AHORA a la lista "${c.lista?.nombre}"? Esta acción dispara los emails reales.`)}>
              🚀 Enviar ahora
            </Boton>
          )}
          {c.estado === 'borrador' && c.programada_at && (
            <Boton variante="secondary" disabled={accionando}
              onClick={() => accion(() => marketingApi.enviarCampana(id, false))}>📅 Dejar programada</Boton>
          )}
          {c.estado === 'enviando' && <Boton variante="secondary" disabled={accionando} onClick={() => accion(() => marketingApi.pausarCampana(id))}>⏸ Pausar</Boton>}
          {c.estado === 'pausada' && <Boton variante="success" disabled={accionando} onClick={() => accion(() => marketingApi.reanudarCampana(id))}>▶ Reanudar</Boton>}
          {!['enviada', 'cancelada'].includes(c.estado) && (
            <Boton variante="destructive" disabled={accionando}
              onClick={() => accion(() => marketingApi.cancelarCampana(id), '¿Cancelar la campaña?')}>✕ Cancelar</Boton>
          )}
          <Boton variante="secondary" disabled={accionando}
            onClick={() => accion(async () => {
              const r = await marketingApi.duplicarCampana(id)
              navigate(`/marketing/campanas/${r.data.id}/editar`)
            })}>⧉ Duplicar</Boton>
        </div>
      </div>

      {err && <p style={st.error}>{err}</p>}

      <div style={st.kpis}>
        {kpis.map(k => (
          <div key={k.label} style={st.kpi}>
            <p style={{ ...st.kpiValor, color: k.alerta ? '#dc2626' : '#0f172a' }}>{k.valor}</p>
            <p style={st.kpiLabel}>{k.label}</p>
          </div>
        ))}
      </div>

      {(m.pendientes ?? 0) > 0 && c.estado === 'enviando' && (
        <p style={st.progreso}>Enviando… quedan {m.pendientes} pendientes (se actualiza solo)</p>
      )}
      {(m.fallidos ?? 0) > 0 && <p style={st.error}>⚠ {m.fallidos} envíos fallidos</p>}

      <h3 style={st.h3}>Vista previa</h3>
      <iframe title="preview" style={st.preview} src={marketingApi.previewCampanaUrl(id)} />
    </div>
  )
}

const st = {
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 16, flexWrap: 'wrap' },
  title: { margin: 0, fontSize: '1.4rem', color: '#0f172a' },
  sub: { margin: '4px 0 0', color: '#64748b', fontSize: '0.88rem' },
  botones: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  kpis: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 16 },
  kpi: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 16px', textAlign: 'center' },
  kpiValor: { margin: 0, fontSize: '1.3rem', fontWeight: 800 },
  kpiLabel: { margin: '4px 0 0', fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' },
  progreso: { color: '#a16207', background: '#fef9c3', padding: '8px 12px', borderRadius: 8, fontSize: '0.88rem' },
  error: { color: '#dc2626', fontSize: '0.875rem' },
  h3: { fontSize: '0.85rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '20px 0 8px' },
  preview: { width: '100%', height: 480, border: '1px solid #e2e8f0', borderRadius: 12, background: '#fff' },
}
